import moment from "moment";

export const getDateFromMilliseconds = (time: number): string => {
    return moment.unix(time / 1000).format("DD.MM.YYYY");
};

/**
 * Long form, as the student result writes it ("24. Juni 2026") — the only locale-*dependent* format
 * in this file, so it names its locale rather than relying on index.tsx's global `moment/locale/de`
 * side effect, which no other entry point (Jest included) performs.
 *
 * "" for a timestamp that is not one: `moment` would otherwise format the literal "Invalid date"
 * straight onto the page, and a caller can hide an empty string but not a plausible-looking lie.
 */
export const getLongDateFromMilliseconds = (time: number): string => {
    const date = moment.unix(time / 1000).locale("de");
    return date.isValid() ? date.format("D. MMMM YYYY") : "";
};

export const getTimeFromMilliseconds = (time: number): string => {
    return moment.unix(time / 1000).format("HH:mm");
};

export const getDateTimeFromMilliseconds = (time: number): string => {
    return moment.unix(time / 1000).format("DD.MM.YYYY - HH:mm");
};

export const convertTimeLimitToSeconds = (timeLimit: string): number => {
    const splitTimeLimit = timeLimit.split(":");
    let totalSeconds = 0;

    if (splitTimeLimit[0] !== "00") {
        totalSeconds += Number(splitTimeLimit[0]) * 3600;
    }

    return totalSeconds + Number(splitTimeLimit[1]) * 60;
};

export const getReadableTimeFromSeconds = (seconds: number): string => {
    const totalSeconds = Math.max(0, Math.floor(seconds));
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (days > 0) {
        return days + "d " + hours + "h " + minutes + "m";
    }

    if (hours > 0) {
        return hours + "h " + minutes + "m";
    }

    return minutes + "m " + secs + "s";
};

export const getReadableTimeFromTimeLimit = (timeLimit: string): string => {
    const [hours, minutes] = timeLimit.split(":");
    return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")} h`;
};

export interface ReadableDurationLabelsParams {
    hour: string;
    hours: string;
    minute: string;
    minutes: string;
}

/**
 * Formats an "HH:MM" time limit as a long-form duration with correct singular/plural,
 * e.g. "10 Minuten", "1 Stunde", "1 Stunde 1 Minute", "2 Stunden 5 Minuten".
 */
export const getReadableDurationFromTimeLimit = (timeLimit: string, labels: ReadableDurationLabelsParams): string => {
    const [hoursPart, minutesPart] = timeLimit.split(":");
    const hours = Number(hoursPart) || 0;
    const minutes = Number(minutesPart) || 0;

    const parts: string[] = [];
    if (hours > 0) {
        parts.push(`${hours} ${hours === 1 ? labels.hour : labels.hours}`);
    }
    if (minutes > 0 || hours === 0) {
        parts.push(`${minutes} ${minutes === 1 ? labels.minute : labels.minutes}`);
    }

    return parts.join(" ");
};

export const isDeadlineAlreadyPassed = (time: number): boolean => {
    return moment.unix(time / 1000).isSameOrBefore();
};

export const getCurrentDateTimeFormatted = (): string => {
    return moment().format("DD.MM.YYYY - HH:mm");
};

export const formatUTCTime = (date: string): string => {
    return moment(date).format("DD.MM.YYYY - HH:mm");
};
